/**
 * CORE-1102 — a poll that stops while the page is hidden.
 *
 * Every background poll in the app was a plain `setInterval`, so a tab left
 * open in another window kept fetching forever. Measured on dev while
 * `document.hidden === true`: `/api/lives/upcoming` came back every 30.004 s,
 * and `unread-summary` twice per cycle — Chrome's own timer throttling never
 * kicked in for us. That is the «аномально быстрый расход батареи» the
 * marketing report opened with: nobody is looking at the page, and the app is
 * still talking to the API.
 *
 * The rule this encodes: a poll exists to keep what the user is LOOKING at
 * fresh. Hidden, there is nothing to keep fresh, so we stop — and because a
 * stopped poll means the first render after switching back would show stale
 * numbers, coming back into view fires a tick immediately instead of waiting
 * out the remaining interval. That is strictly fresher than the old behaviour,
 * not just cheaper.
 *
 * Off web (`document` undefined — native, jest without a DOM) there is no
 * visibility signal, so this degrades to exactly `setInterval`.
 */

export interface VisibilityAwareIntervalOptions {
  /** The poll body. Never called while the page is hidden. */
  onTick: () => void;
  /** Cadence while visible. */
  intervalMs: number;
  /**
   * Injected in tests. Defaults to the real `document`; pass `null` to force
   * the no-visibility-signal path.
   */
  doc?: VisibilityDocument | null;
}

/** The slice of `document` this helper touches — keeps it mockable and native-safe. */
export interface VisibilityDocument {
  hidden: boolean;
  addEventListener: (type: string, listener: () => void) => void;
  removeEventListener: (type: string, listener: () => void) => void;
}

const resolveDoc = (
  provided: VisibilityDocument | null | undefined,
): VisibilityDocument | null => {
  if (provided !== undefined) return provided;
  // I-2 — shared code must not assume a DOM. Native and SSR land here.
  if (typeof document === 'undefined') return null;
  return document as unknown as VisibilityDocument;
};

/**
 * Starts the poll. Returns the stop function — call it from the effect cleanup
 * exactly like `clearInterval`; it also unbinds the visibility listener, so a
 * caller that stops has nothing left running.
 */
export function startVisibilityAwareInterval({
  onTick,
  intervalMs,
  doc,
}: VisibilityAwareIntervalOptions): () => void {
  const target = resolveDoc(doc);
  let timer: ReturnType<typeof setInterval> | null = null;
  let stopped = false;

  // A visible poll always begins with a tick — starting one is how a caller
  // says «I need this data now», and every caller here did its own leading
  // fetch before this helper existed. Hidden, it stays silent until the page
  // comes back, and the resume path below supplies the tick instead.
  const run = () => {
    if (stopped || timer !== null) return;
    onTick();
    timer = setInterval(onTick, intervalMs);
  };
  const pause = () => {
    if (timer === null) return;
    clearInterval(timer);
    timer = null;
  };

  // No visibility signal — behave as a plain interval and nothing else.
  if (!target) {
    run();
    return () => {
      stopped = true;
      pause();
    };
  }

  const onVisibilityChange = () => {
    if (stopped) return;
    if (target.hidden) {
      pause();
      return;
    }
    // Back in view: `run` leads with a tick, so the numbers are caught up
    // before the user has finished looking at them.
    run();
  };

  if (!target.hidden) run();
  target.addEventListener('visibilitychange', onVisibilityChange);

  return () => {
    stopped = true;
    pause();
    target.removeEventListener('visibilitychange', onVisibilityChange);
  };
}
